/**
 * FairwayBid – Bündelausschreibung
 *
 * Eine einzelne Anlage bestellt 6.000 Range-Bälle und zahlt Kleinmengenpreis.
 * Drei Anlagen, die im selben Zeitraum dasselbe suchen, bestellen zusammen
 * 18.000 – das ist eine andere Preisklasse. Genau diese Einkaufsmacht hat ein
 * einzelner Betrieb nicht, und sie ist der Grund, warum eine Plattform hier
 * mehr kann als drei Telefonate.
 *
 * Technisch führen wir keine Anfragen zusammen: Jede Anlage bleibt Eigentümer
 * ihrer eigenen Ausschreibung, jede entscheidet selbst über ihr Angebot. Wir
 * zeigen nur, dass da etwas zusammengehört – der Anlage, damit sie es weiß,
 * dem Händler, damit er ein Sammelangebot rechnen kann.
 *
 * Die Zustimmung steht schon in der Anfrage: das Feld "Bündelbestellung
 * möglich". Wer dort Nein sagt, taucht in keinem Bündel auf.
 */

import { getDeadlineStatus } from './deadlines';
import { MERKMAL_MATCH_FIELDS } from './merkmale';

// "Auf Anfrage" zählt als offen – der Haendler darf fragen, mehr passiert nicht.
export const BUNDLE_OPEN_VALUES = ['yes', 'on_request'];

const publicData = listing => listing?.attributes?.publicData || {};

export const isBundleOpen = listing =>
  BUNDLE_OPEN_VALUES.includes(publicData(listing).bundle_allowed);

export const getCategory = listing => {
  const pd = publicData(listing);
  return pd.categoryLevel1 || pd.categoryLevel2 || pd.category || null;
};

export const getCountry = listing => publicData(listing).delivery_country || null;

export const getAuthorId = listing =>
  listing?.author?.id?.uuid || listing?.relationships?.author?.data?.id?.uuid || null;

/**
 * Stueckzahl, wo die Anfrage eine nennt.
 *
 * Zwei Felder, weil zwei Faelle: Range-Baelle haben eine Staffel, weil dort
 * die Menge ueber den Preis entscheidet; alle anderen Kategorien haben ein
 * freies Zahlenfeld (siehe quantityField.js). Wo keins von beiden gefuellt
 * ist, geben wir null zurueck - lieber gar keine Summe als eine erfundene.
 */
export const getQuantity = listing => {
  const pd = publicData(listing);
  const raw = pd.listing_how_many_range_golfballs ?? pd.menge;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
};

/** Läuft die Angebotsfrist noch? Eine abgelaufene Anfrage bündelt niemand. */
export const isStillOpen = (listing, now) => {
  const status = getDeadlineStatus(listing, now);
  return !status || !status.expired;
};

/**
 * Was innerhalb einer Kategorie gleich sein MUSS, damit ein Bündel überhaupt
 * bestellbar ist.
 *
 * Bei Range-Bällen sind das Ausführung und Farbe: Ein Anbieter kann 18.000
 * zweistückige gelbe Bälle in einer Charge liefern – 6.000 gelbe, 6.000 weiße
 * und 6.000 einstückige sind drei Bestellungen mit drei Preisen. Die Menge
 * dagegen ist frei; sie ist ja gerade der Hebel.
 *
 * Bei allen anderen Kategorien ist nur das Produkt selbst bindend. Ob ein
 * Ballautomat mit Kartenzahlung oder mit Chip laufen soll, kann jede Anlage
 * für sich entscheiden, ohne dass die gemeinsame Bestellung daran scheitert.
 *
 * Neue Kategorie mit Zwang: Schlüssel eintragen, sonst nichts.
 */
export const BUNDLE_MATCH_FIELDS = {
  'range-golfballs-listing-type': ['kind_of_range_golfballs', 'range_golfball_color'],
  // Alle uebrigen Kategorien bringen ihre bindenden Merkmale selbst mit -
  // Antriebsart und Sitzplaetze beim Cart, Bauart bei der Waschmaschine,
  // Format bei den Matten. Welche das sind, steht bei den Feldern (siehe
  // merkmale.js) und nicht hier: Eine zweite Liste wuerde eines Tages anders
  // denken als das Formular.
  ...MERKMAL_MATCH_FIELDS,
};

/**
 * Angaben, die bei einer Bündelbestellung ausdrücklich NICHT gemeinsam sind,
 * obwohl sie zum Produkt gehören.
 *
 * Der Logodruck ist der Fall, um den es geht: Drei Anlagen bestellen dieselben
 * Bälle in einer Charge, aber jede will ihr eigenes Logo darauf. Das lässt
 * sich nicht vorher im Bündel festlegen – es wird nach der Bestellung
 * zwischen jeder Anlage und dem Anbieter einzeln vereinbart.
 *
 * Deshalb wird das Feld beim Anhängen nicht übernommen (siehe
 * bundleDraft.js), und deshalb steht der Hinweis dazu im Pool und im
 * Formular. Wer es nicht weiß, rechnet mit einem Preis, der so nicht gilt.
 */
export const BUNDLE_HINWEISE = {
  'range-golfballs-listing-type':
    'Der Logodruck gehört nicht zum Bündel: Die Bälle kommen aus einer Charge, den Druck vereinbart jede Anlage nach der Bestellung einzeln mit dem Anbieter.',
};

/** Der Hinweis zu dieser Kategorie, oder null. */
export const bundleHinweis = category => BUNDLE_HINWEISE[category] || null;

/** Die bindenden Felder dieser Kategorie – leer, wenn es keine gibt. */
export const matchFelderFuer = category => BUNDLE_MATCH_FIELDS[category] || [];

/*
  Mehrfachauswahlen kommen als Feld an ("gelb" und "weiß"). Fuer den Vergleich
  wird sortiert und zusammengezogen: Zwei Anlagen, die dasselbe angekreuzt
  haben, sollen zusammenfinden, auch wenn sie in anderer Reihenfolge geklickt
  haben.
*/
const normiert = wert => {
  if (Array.isArray(wert))
    return [...wert]
      .filter(Boolean)
      .sort()
      .join('+');
  return wert === null || wert === undefined ? '' : String(wert);
};

/** Die bindenden Merkmale einer Anfrage, als Objekt. */
export const matchWerte = listing => {
  const pd = publicData(listing);
  const felder = matchFelderFuer(getCategory(listing));
  return felder.reduce((acc, key) => ({ ...acc, [key]: normiert(pd[key]) }), {});
};

/**
 * Der Schlüssel, unter dem zwei Anfragen ein Bündel bilden können.
 * Gleiche Kategorie und gleiche bindende Merkmale – sonst nichts.
 */
/**
 * Ist diese Anfrage überhaupt bündelfähig?
 *
 * Ein Bündel ist EINE Bestellung aus EINER Charge. Die Felder, die ein Bündel
 * binden, müssen deshalb eindeutig beantwortet sein – bei Range-Bällen also
 * genau eine Ausführung und genau eine Farbe.
 *
 * "Weiß oder Gelb" ist als einzelne Anfrage völlig in Ordnung und sogar
 * klug: Der Anbieter nimmt, was er da hat. Für ein Bündel taugt es nicht.
 * Drei Anlagen, von denen eine "Weiß", eine "Gelb" und eine "beides" sagt,
 * ergeben keine Charge, sondern eine Rückfrage – und der Anbieter müsste
 * raten, was er kalkuliert.
 *
 * Deshalb: Mehrfachauswahl in einem bindenden Feld schließt die Anfrage vom
 * Pool aus, nicht von der Ausschreibung.
 */
export const istBuendelfaehig = listing => {
  const category = getCategory(listing);
  if (!category) return false;
  const pd = publicData(listing);

  return matchFelderFuer(category).every(key => {
    const wert = pd[key];
    if (Array.isArray(wert)) return wert.filter(Boolean).length === 1;
    return wert !== null && wert !== undefined && wert !== '';
  });
};

/**
 * Welche bindenden Felder mehrdeutig beantwortet sind.
 *
 * Für den Hinweis an der eigenen Anfrage: "Ihre Anfrage nennt zwei Farben"
 * ist eine Auskunft, "nicht bündelfähig" wäre ein Rätsel.
 */
export const mehrdeutigeFelder = listing => {
  const category = getCategory(listing);
  if (!category) return [];
  const pd = publicData(listing);

  return matchFelderFuer(category).filter(key => {
    const wert = pd[key];
    return Array.isArray(wert) && wert.filter(Boolean).length > 1;
  });
};

/**
 * Welche bindenden Felder gar nicht beantwortet sind.
 *
 * Der Unterschied zu mehrdeutigeFelder: Dort stehen zwei Antworten, hier
 * keine. Fuer die Anlage ist das zweierlei - "Sie haben zwei Farben genannt"
 * und "Die Antriebsart fehlt" fuehren zu verschiedenen Handgriffen.
 */
export const fehlendeFelder = listing => {
  const category = getCategory(listing);
  if (!category) return [];
  const pd = publicData(listing);

  return matchFelderFuer(category).filter(key => {
    const wert = pd[key];
    if (Array.isArray(wert)) return wert.filter(Boolean).length === 0;
    return wert === null || wert === undefined || wert === '';
  });
};

export const bundleKey = listing => {
  const category = getCategory(listing);
  if (!category) return null;
  const werte = matchWerte(listing);
  const teile = matchFelderFuer(category).map(k => `${k}=${werte[k]}`);
  return [category, ...teile].join('|');
};

/**
 * Wie viele Anlagen höchstens in einem Bündel stehen.
 *
 * Drei. Eine gemeinsame Bestellung will abgestimmt sein – Liefertermin,
 * Rechnungen, bei Bällen der Logodruck pro Anlage. Ab der vierten Anlage
 * kostet die Abstimmung mehr, als die größere Menge einbringt.
 *
 * Gezählt werden Anlagen, nicht Anfragen: Zwei Anfragen derselben Anlage
 * sind eine Anlage.
 */
export const MAX_ANLAGEN = 3;

const zeitstempel = listing => {
  const a = listing?.attributes || {};
  const roh = a.publishedAt || a.createdAt;
  const t = roh ? new Date(roh).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
};

/**
 * Teilt eine Gruppe gleichartiger Anfragen in Bündel zu höchstens MAX_ANLAGEN.
 *
 * Ohne diese Aufteilung würde aus einer Obergrenze eine Lüge: Suchen fünf
 * Anlagen dasselbe, entsteht sonst eine Kachel mit fünf Anlagen, auf der
 * "höchstens drei" steht. Stattdessen werden daraus zwei Bündel – eines
 * voll, eines mit Platz.
 *
 * Wer zuerst ausgeschrieben hat, ist zuerst drin: Die Reihenfolge ist die
 * Veröffentlichung, nicht der Zufall der Suchtreffer. Und die Anfragen einer
 * Anlage bleiben zusammen - eine Anlage über zwei Bündel zu verteilen wäre
 * genau die Abstimmung, die die Grenze vermeiden soll.
 *
 * @param {Array} listings gleichartige Anfragen
 * @param {Function} anlageVon liefert die Anlagen-Kennung einer Anfrage
 * @returns {Array<Array>} die Bündel, das erste zuerst
 */
export const teileNachAnlagen = (listings = [], anlageVon = getAuthorId) => {
  const nachAnlage = new Map();
  [...(Array.isArray(listings) ? listings : [])]
    .sort((a, b) => zeitstempel(a) - zeitstempel(b))
    .forEach(l => {
      const schluessel = anlageVon(l) || l?.id?.uuid || 'unbekannt';
      if (!nachAnlage.has(schluessel)) nachAnlage.set(schluessel, []);
      nachAnlage.get(schluessel).push(l);
    });

  const buendel = [];
  nachAnlage.forEach(anfragen => {
    const letztes = buendel[buendel.length - 1];
    if (!letztes || letztes.anlagen >= MAX_ANLAGEN) {
      buendel.push({ anlagen: 1, listings: [...anfragen] });
      return;
    }
    letztes.anlagen += 1;
    letztes.listings.push(...anfragen);
  });

  return buendel.map(b => b.listings);
};

/**
 * Anfragen, die mit dieser ein Buendel bilden koennten: gleiche Kategorie,
 * gleiches Lieferland, beide fuer Buendel offen, beide Fristen noch offen und
 * eine andere Anlage dahinter.
 */
export const findBundlePartners = (listing, candidates = [], options = {}) => {
  // Frueher stand hier als dritter Parameter direkt das Datum. Damit ein
  // alter Aufruf nicht stillschweigend eine leere Option bekommt, wird er
  // weiter verstanden.
  const opts = options instanceof Date ? { now: options } : options || {};
  const { eigeneAnlageId = null, now = new Date() } = opts;

  if (!listing || !isBundleOpen(listing) || !isStillOpen(listing, now)) return [];
  // Eine mehrdeutige Anfrage kann kein Bündel anführen - und keinem beitreten.
  if (!istBuendelfaehig(listing)) return [];

  // Nicht mehr nur die Kategorie: Bei Range-Baellen muessen auch Ausfuehrung
  // und Farbe stimmen, sonst ist die gemeinsame Bestellung keine.
  const key = bundleKey(listing);
  const country = getCountry(listing);
  // Die eigenen Anzeigen kommen aus dem Duck "Anzeigen verwalten" und tragen
  // keine Autorenangabe - dort ist der Autor immer der angemeldete Nutzer.
  // Ohne diesen Ersatz faellt die Sperre gegen die eigene Anlage lautlos aus,
  // und zwei eigene Anfragen derselben Art zaehlen als zwei Anlagen.
  const ownerId = getAuthorId(listing) || eigeneAnlageId;
  const ownId = listing?.id?.uuid;

  const passend = (Array.isArray(candidates) ? candidates : []).filter(other => {
    if (!other || other?.id?.uuid === ownId) return false;
    if (!key || bundleKey(other) !== key) return false;
    if (country && getCountry(other) && getCountry(other) !== country) return false;
    if (ownerId && getAuthorId(other) === ownerId) return false;
    return isBundleOpen(other) && isStillOpen(other, now) && istBuendelfaehig(other);
  });

  // Ein Bündel fasst MAX_ANLAGEN Anlagen, die eigene eingerechnet. Passen
  // mehr, gehören sie in ein zweites Bündel und nicht in diesen Satz - sonst
  // stünde an der eigenen Anfrage "vier weitere Anlagen suchen dasselbe",
  // während der Pool nebenan eine Obergrenze von drei behauptet.
  const fremdeAnlagen = new Set();
  return [...passend]
    .sort((a, b) => zeitstempel(a) - zeitstempel(b))
    .filter(other => {
      const anlage = getAuthorId(other) || other?.id?.uuid;
      if (fremdeAnlagen.has(anlage)) return true;
      if (fremdeAnlagen.size >= MAX_ANLAGEN - 1) return false;
      fremdeAnlagen.add(anlage);
      return true;
    });
};

/**
 * Kennzahlen eines Buendels aus Sicht einer Anfrage.
 *
 * @returns {{count: number, totalQuantity: number|null, partialQuantity: boolean}}
 *          count = Anzahl der Anlagen inklusive der eigenen
 */
export const summarizeBundle = (listing, partners = []) => {
  const all = [listing, ...(Array.isArray(partners) ? partners : [])].filter(Boolean);
  const quantities = all.map(getQuantity);
  const known = quantities.filter(q => q !== null);

  const owners = new Set(all.map(getAuthorId).filter(Boolean));

  return {
    count: all.length,
    // Zwei Anfragen koennen von derselben Anlage kommen - fuer die Aussage
    // "gemeinsam bestellen" zaehlen die Anlagen, nicht die Anfragen.
    ownerCount: owners.size || all.length,
    totalQuantity: known.length > 0 ? known.reduce((a, b) => a + b, 0) : null,
    // Nicht alle haben eine Menge genannt: dann ist die Summe eine Untergrenze.
    partialQuantity: known.length > 0 && known.length < all.length,
  };
};

/**
 * Alle Buendel in einer Menge von Anfragen – fuer die Haendlersicht, wo mehrere
 * Anfragen nebeneinander liegen. Nur Gruppen ab zwei Anlagen.
 *
 * @returns {Array<{category: string, country: string|null, listings: Array}>}
 */
export const groupBundles = (listings = [], now = new Date()) => {
  const open = (Array.isArray(listings) ? listings : []).filter(
    l => isBundleOpen(l) && isStillOpen(l, now) && istBuendelfaehig(l)
  );

  const groups = {};
  open.forEach(listing => {
    const bk = bundleKey(listing);
    if (!bk) return;
    const country = getCountry(listing) || '';
    const key = `${bk}|${country}`;
    if (!groups[key]) {
      groups[key] = {
        category: getCategory(listing),
        country: country || null,
        merkmale: matchWerte(listing),
        listings: [],
      };
    }
    groups[key].listings.push(listing);
  });

  return (
    Object.values(groups)
      // Auch in der Haendlersicht gilt die Obergrenze: Sonst kalkuliert ein
      // Anbieter eine Sammelbestellung ueber fuenf Anlagen, die es so nicht
      // geben wird.
      .flatMap(group => teileNachAnlagen(group.listings).map(listings => ({ ...group, listings })))
      .filter(group => {
        const owners = new Set(group.listings.map(getAuthorId).filter(Boolean));
        group.ownerCount = owners.size || group.listings.length;
        // Zwei Anfragen derselben Anlage sind kein Buendel.
        return group.listings.length >= 2 && (owners.size === 0 || owners.size >= 2);
      })
      .sort((a, b) => b.listings.length - a.listings.length)
  );
};

/**
 * Steht diese eigene Anfrage wirklich im Pool?
 *
 * Vier Bedingungen, und die erste wird leicht vergessen: Der Pool ist eine
 * Suche, und Suchen finden nur veröffentlichte Anzeigen. Ein Entwurf, eine
 * Anfrage in Prüfung oder eine geschlossene stehen dort nicht – auch wenn
 * die Bündelfreigabe gesetzt ist und die Frist noch läuft.
 *
 * Das ist der Unterschied zur Poolseite selbst: Dort kommen die Anfragen
 * aus der Suche und sind deshalb zwangsläufig veröffentlicht. Hier kommen
 * sie aus den eigenen Anzeigen, und dort liegt alles nebeneinander.
 */
export const stehtImPool = (listing, now = new Date()) =>
  listing?.attributes?.state === 'published' &&
  isBundleOpen(listing) &&
  isStillOpen(listing, now) &&
  istBuendelfaehig(listing);

/**
 * Der Bündel-Pool: alles, was gerade offen für eine gemeinsame Bestellung ist –
 * unabhängig davon, ob die Anlage selbst schon eine passende Anfrage hat.
 *
 * Das ist der Unterschied zu groupBundles(): Dort geht es um Bündel, die schon
 * bestehen, hier um welche, die entstehen könnten. Deshalb reicht hier eine
 * einzige fremde Anfrage – wer sich anhängt, ist die zweite.
 *
 * Gruppiert wird nach dem Bündelschlüssel (Kategorie plus die Merkmale, die
 * gleich sein müssen), nicht nach Lieferland. Die Länder stehen
 * an der Gruppe, damit man sieht, wohin geliefert werden müsste; sie vorher
 * auseinanderzusortieren würde aus einem kleinen Pool drei leere machen.
 *
 * Die eigenen Anfragen werden nicht mehr herausgefiltert, sondern markiert.
 * Herausfiltern war die naheliegende Lösung für "man kann sich nicht an sich
 * selbst anhängen" - aber es beantwortet die Frage falsch. Wer seine Anfrage
 * für Bündelung freigegeben hat, will sehen, DASS sie im Pool steht und
 * anderen angezeigt wird. Verschwindet sie dort, sieht es aus, als hätte die
 * Freigabe nicht gewirkt.
 *
 * Deshalb liefert jede Gruppe beides getrennt:
 *
 *   listings   die Anfragen ANDERER Anlagen - nur an die hängt man sich an
 *   eigene     die eigenen Anfragen in dieser Gruppe
 *   istDabei   die eigene Anlage ist schon Teil dieses Bündels
 *   nurEigene  nichts als die eigene Anfrage - eine Statusmeldung
 *
 * Erkannt wird die eigene Anfrage auf zwei Wegen, weil einer allein versagen
 * kann: Kommt die Suche ohne die Autorenangabe zurück (der "include"-Parameter
 * fehlt, ein Feld wird gespart), greift eigeneAnlageId nicht mehr;
 * eigeneAnfrageIds braucht diese Angabe nicht.
 *
 * @param {Array} listings Anfragen aus der Suche
 * @param {Object} options eigeneAnlageId, eigeneAnfrageIds
 * @returns {Array} Gruppen, die größte zuerst
 */
export const poolGroups = (listings = [], options = {}) => {
  const { eigeneAnlageId = null, eigeneAnfrageIds = [], now = new Date() } = options;
  const eigeneIds = new Set(
    (Array.isArray(eigeneAnfrageIds) ? eigeneAnfrageIds : []).filter(Boolean)
  );

  // Zwei Erkennungswege, weil einer allein versagen kann: Kommt die Suche
  // ohne Autorenangabe zurueck, greift der zweite; kennt der Aufrufer die
  // eigenen Anfragen nicht, greift der erste.
  const istEigen = l =>
    eigeneIds.has(l?.id?.uuid) || !!(eigeneAnlageId && getAuthorId(l) === eigeneAnlageId);

  const offen = (Array.isArray(listings) ? listings : []).filter(
    l => isBundleOpen(l) && isStillOpen(l, now) && istBuendelfaehig(l)
  );

  const gruppen = {};
  offen.forEach(listing => {
    const key = bundleKey(listing);
    if (!key) return;
    if (!gruppen[key]) {
      gruppen[key] = {
        key,
        category: getCategory(listing),
        merkmale: matchWerte(listing),
        listings: [],
        countries: new Set(),
      };
    }
    gruppen[key].listings.push(listing);
    const land = getCountry(listing);
    if (land) gruppen[key].countries.add(land);
  });

  // Die Anlagen-Kennung: Bei eigenen Anfragen kann die Autorenangabe fehlen,
  // dann steht die eigene Anlage dafuer - sonst gaelte man beim Aufteilen als
  // mehrere Anlagen.
  const anlageVon = l => getAuthorId(l) || (istEigen(l) ? eigeneAnlageId || 'eigene' : null);

  return (
    Object.values(gruppen)
      // Aus einer Gruppe gleichartiger Anfragen koennen mehrere Buendel
      // werden: Drei Anlagen sind das Maximum, der Rest bildet das naechste.
      .flatMap(gruppe => {
        const teile = teileNachAnlagen(gruppe.listings, anlageVon);
        return teile.map((listings, i) => ({
          ...gruppe,
          // Eigener Schluessel je Buendel - React braucht ihn, und das
          // Mengenfeld haengt daran.
          key: teile.length > 1 ? `${gruppe.key}#${i + 1}` : gruppe.key,
          nummer: teile.length > 1 ? i + 1 : null,
          vonWievielen: teile.length > 1 ? teile.length : null,
          listings,
          countries: new Set(listings.map(getCountry).filter(Boolean)),
        }));
      })
      .map(gruppe => {
        const alle = gruppe.listings;
        const eigene = alle.filter(istEigen);
        const fremde = alle.filter(l => !istEigen(l));

        const mengen = alle.map(getQuantity);
        const bekannt = mengen.filter(q => q !== null);
        // Eine eigene Anfrage kann ohne Autorenangabe dastehen. Sie trotzdem
        // mitzuzaehlen ist wichtig: Sonst heisst es "eine Anlage sucht", wo
        // zwei zusammenkaemen.
        const anlagen = new Set(
          alle
            .map(l => getAuthorId(l) || (istEigen(l) ? eigeneAnlageId || 'eigene' : null))
            .filter(Boolean)
        );

        const ownerCount = anlagen.size || alle.length;

        return {
          key: gruppe.key,
          // Bei mehreren Buendeln derselben Art: das wievielte von wie vielen.
          nummer: gruppe.nummer,
          vonWievielen: gruppe.vonWievielen,
          category: gruppe.category,
          hinweis: bundleHinweis(gruppe.category),
          // Was diese Gruppe von einer anderen derselben Kategorie unterscheidet.
          merkmale: gruppe.merkmale,
          // Was ANDERE Anlagen suchen. Die eigenen Anfragen stehen daneben,
          // nicht darin - an die eigene haengt man sich nicht an.
          listings: fremde,
          eigene,
          istDabei: eigene.length > 0,
          nurEigene: eigene.length > 0 && fremde.length === 0,
          countries: [...gruppe.countries],
          ownerCount,
          // Voll heisst: Hier passt keine Anlage mehr dazu.
          voll: ownerCount >= MAX_ANLAGEN,
          freiePlaetze: Math.max(0, MAX_ANLAGEN - ownerCount),
          totalQuantity: bekannt.length > 0 ? bekannt.reduce((a, b) => a + b, 0) : null,
          partialQuantity: bekannt.length > 0 && bekannt.length < alle.length,
        };
      })
      // Wo etwas zu tun ist, steht oben. Die eigene Anfrage allein ist eine
      // Statusmeldung, keine Gelegenheit - sie steht am Ende.
      .sort(
        (a, b) =>
          // Wo man mitmachen kann, steht oben; volle Buendel und die eigene
          // Anfrage allein sind Auskunft, keine Gelegenheit.
          Number(a.nurEigene) - Number(b.nurEigene) ||
          Number(a.voll && !a.istDabei) - Number(b.voll && !b.istDabei) ||
          b.ownerCount - a.ownerCount ||
          b.listings.length - a.listings.length
      )
  );
};
